import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Pin, Eye, FileText, Clock, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const map = {
    published: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    draft: 'bg-slate-700/40 text-slate-400 border-slate-600',
    archived: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};

const AdminKnowledge = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/knowledge/articles`, { headers: authHeaders() });
      setArticles(data.articles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/knowledge/articles/${id}`, { headers: authHeaders() });
      toast.success('Article deleted');
      fetchArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = articles.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!a.title.toLowerCase().includes(q) && !(a.hashtags || []).some(t => t.includes(q))) return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6" data-testid="admin-knowledge-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Knowledge</h1>
          <p className="text-sm text-slate-400 mt-1">Manage blog articles published at <code className="text-amber-400">/knowledge</code></p>
        </div>
        <Button onClick={() => navigate('/admin/knowledge/new')} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="new-article-btn">
          <Plus className="w-4 h-4 mr-1" />New Article
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or hashtag..."
            className="pl-10 bg-slate-900 border-slate-700 text-slate-100"
            data-testid="article-search-input"
          />
        </div>
        <div className="flex gap-1 p-1 bg-slate-900 rounded-lg border border-slate-700">
          {['all', 'published', 'draft', 'archived'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition ${
                statusFilter === s ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-slate-100'
              }`}
              data-testid={`status-filter-${s}`}
            >
              {s} {s !== 'all' && `(${articles.filter(a => a.status === s).length})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Loading articles...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-800 rounded-lg">
          <FileText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">No articles yet.</p>
          <Button onClick={() => navigate('/admin/knowledge/new')} className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-950">
            <Plus className="w-4 h-4 mr-1" />Create your first article
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-slate-800 border border-slate-800 rounded-lg overflow-hidden bg-slate-900/40">
          {filtered.map(a => (
            <div key={a.id} className="group flex gap-4 p-4 hover:bg-slate-900/80 transition-colors" data-testid={`article-row-${a.slug}`}>
              <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
                {a.cover_image ? (
                  <img src={a.cover_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600"><FileText className="w-6 h-6" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <StatusBadge status={a.status} />
                  {a.pinned && <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Pin className="w-3 h-3" />Pinned</span>}
                  {a.views ? <span className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" />{a.views}</span> : null}
                </div>
                <h3 className="text-base font-semibold text-slate-100 mb-1">{a.title}</h3>
                <p className="text-sm text-slate-400 line-clamp-1 mb-1">{a.excerpt}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {(a.hashtags || []).map(t => <span key={t}>#{t}</span>)}
                  {a.updated_at && (
                    <span className="ml-auto flex items-center gap-1">
                      <Clock className="w-3 h-3" />{new Date(a.updated_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Link to={`/knowledge/${a.slug}`} target="_blank" className="p-2 text-slate-400 hover:text-amber-400" title="Preview"><Eye className="w-4 h-4" /></Link>
                <button onClick={() => navigate(`/admin/knowledge/edit/${a.id}`)} className="p-2 text-slate-400 hover:text-amber-400" title="Edit" data-testid={`edit-article-${a.slug}`}><Edit className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(a.id, a.title)} className="p-2 text-slate-400 hover:text-red-400" title="Delete" data-testid={`delete-article-${a.slug}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminKnowledge;
